import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Award, GitBranch, Workflow, Brain } from 'lucide-react';
import GraphRAGResearch from './GraphRAGResearch';
import AIWorkflowResearch from './AIWorkflowResearch';

const InnovationChallenge2024 = ({ onClose }) => {
  const [showGraphRAGDetail, setShowGraphRAGDetail] = useState(false);
  const [showAIWorkflowDetail, setShowAIWorkflowDetail] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="modal-content max-w-5xl max-h-[85vh] overflow-y-auto pr-2 custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>×</button>

        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold mb-2">🏆 2024 Innovation Challenge</h2>
          <p className="text-gray-300">Rule Master: RAG-based Rule Generation & Workflow Control</p>
        </div>

        {/* Champion Photo & Solution Architecture - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Champion Photo */}
          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-6 border border-yellow-500/30">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-6 h-6 text-yellow-400" />
              <h3 className="text-xl font-bold text-yellow-400">🎖️ Champion Team</h3>
            </div>
            <div className="relative rounded-lg overflow-hidden border-2 border-yellow-500/30 bg-gray-900/50">
              <motion.img
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                src="/pic/innovation/2024/rule_master/chanmpion.webp"
                alt="Champion Team Photo"
                className="w-full h-auto object-contain max-h-[250px]"
              />
            </div>
          </div>

          {/* Solution Architecture */}
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-6 h-6 text-blue-400" />
              <h3 className="text-xl font-bold text-blue-400">🏗️ Solution Architecture</h3>
            </div>
            <div className="relative rounded-lg overflow-hidden border-2 border-blue-500/30 bg-gray-900/50">
              <motion.img
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                src="/pic/innovation/2024/rule_master/architecture.webp"
                alt="Solution Architecture"
                className="w-full h-auto object-contain max-h-[250px]"
              />
            </div>
          </div>
        </div>

        {/* Competition Description */}
        <div className="bg-white/5 rounded-xl p-6 border border-purple-500/30 mb-6">
          <p className="text-gray-300 text-center">
            Award-winning solution for <span className="text-cyan-400 font-bold">RAG-based rule generation</span> and <span className="text-green-400 font-bold">natural language workflow control</span>
          </p>
        </div>

        {/* Research Journey Introduction */}
        <div className="bg-white/5 rounded-xl p-6 border border-purple-500/30 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="w-6 h-6 text-purple-400" />
            <h3 className="text-xl font-bold text-purple-400">🔬 Post-Competition Research Journey</h3>
          </div>
          <p className="text-gray-300 mb-4">
            Building upon the <span className="text-yellow-400 font-bold">Innovation Challenge success</span>, our research expanded into two complementary directions:
          </p>
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20 mb-4">
            <p className="text-sm text-gray-300 text-center">
              The competition's <span className="text-cyan-400">RAG-based rule generation</span> inspired deeper research into knowledge graphs, while the <span className="text-green-400">natural language workflow control</span> led to intelligent automation systems.
            </p>
          </div>

          {/* Research Paths - Clickable Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <motion.div
              className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-lg p-5 border-2 border-cyan-500/50 cursor-pointer hover:border-cyan-400 transition-all relative overflow-hidden group"
              onClick={() => setShowGraphRAGDetail(true)}
              whileHover={{ scale: 1.02 }}
              style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.3)' }}
            >
              {/* Breathing glow effect - always active */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 animate-breathing"></div>

              {/* Pulsing border glow */}
              <div className="absolute inset-0 border-2 border-cyan-400/50 rounded-lg animate-pulse-glow"></div>


              <div className="relative">
                <h4 className="font-bold text-cyan-400 mb-2 flex items-center gap-2">
                  <GitBranch className="w-5 h-5" />
                  RAG Evolution Path
                </h4>
                <p className="text-sm text-gray-300 mb-3">
                  From Rule Generation to GraphRAG Exploration
                </p>
                <div className="flex items-center text-cyan-400 text-sm font-semibold">
                  <span className="mr-2">Click to Explore</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-lg p-5 border-2 border-green-500/50 cursor-pointer hover:border-green-400 transition-all relative overflow-hidden group"
              onClick={() => setShowAIWorkflowDetail(true)}
              whileHover={{ scale: 1.02 }}
              style={{ boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)' }}
            >
              {/* Breathing glow effect - always active */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/30 to-emerald-500/30 animate-breathing"></div>

              {/* Pulsing border glow */}
              <div className="absolute inset-0 border-2 border-green-400/50 rounded-lg animate-pulse-glow"></div>


              <div className="relative">
                <h4 className="font-bold text-green-400 mb-2 flex items-center gap-2">
                  <Workflow className="w-5 h-5" />
                  AI Workflow Path
                </h4>
                <p className="text-sm text-gray-300 mb-3">
                  From NL Control to Intelligent Automation
                </p>
                <div className="flex items-center text-green-400 text-sm font-semibold">
                  <span className="mr-2">Click to Explore</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* GraphRAG Research Detail Modal */}
        <AnimatePresence>
          {showGraphRAGDetail && (
            <GraphRAGResearch onClose={() => setShowGraphRAGDetail(false)} />
          )}
        </AnimatePresence>

        {/* AI Workflow Research Detail Modal */}
        <AnimatePresence>
          {showAIWorkflowDetail && (
            <AIWorkflowResearch onClose={() => setShowAIWorkflowDetail(false)} />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default InnovationChallenge2024;
